import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createManualInvoice } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PaymentTermsField from '../components/PaymentTermsField';
import { Plus, Trash2, FileText, Loader2, Download, ArrowLeft, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ManualInvoicePage = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [createdId, setCreatedId] = useState(null);

  const [form, setForm] = useState({
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
    client_name: '',
    client_address: '',
    client_email: '',
    currency: 'CHF',
    vat_enabled: false,
    payment_terms: '',
    accepted_payment_methods: ['virement'],
    notes: '',
    status: 'sent',
    is_acquittee: false,
    payment_date: new Date().toISOString().split('T')[0],
    payment_method_used: 'virement',
    payment_reference: '',
    installments_enabled: false,
    installments_count: 3,
    installments_start: new Date().toISOString().split('T')[0],
  });

  const [items, setItems] = useState([{ description: '', quantity: 1, unit_price: 0 }]);

  const addItem = () => setItems([...items, { description: '', quantity: 1, unit_price: 0 }]);
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const updateItem = (idx, field, value) => {
    const next = [...items];
    next[idx] = { ...next[idx], [field]: field === 'description' ? value : (parseFloat(value) || 0) };
    setItems(next);
  };

  const subtotal = items.reduce((s, i) => s + i.quantity * i.unit_price, 0);
  const vatRate = 8.1;
  const vatAmount = form.vat_enabled ? subtotal * vatRate / 100 : 0;
  const total = subtotal + vatAmount;

  const fmt = (n) => new Intl.NumberFormat('fr-CH', { minimumFractionDigits: 2 }).format(n) + ' ' + form.currency;

  // Generate installments schedule
  const installments = useMemo(() => {
    if (!form.installments_enabled || form.installments_count < 2 || total <= 0) return [];
    const count = Math.min(12, Math.max(2, form.installments_count));
    const perMonth = Math.floor(total * 100 / count) / 100;
    const last = Math.round((total - perMonth * (count - 1)) * 100) / 100;
    const start = new Date(form.installments_start || Date.now());
    return Array.from({ length: count }, (_, i) => {
      const d = new Date(start);
      d.setMonth(d.getMonth() + i);
      return {
        num: i + 1,
        date: d.toISOString().split('T')[0],
        amount: i === count - 1 ? last : perMonth,
      };
    });
  }, [form.installments_enabled, form.installments_count, form.installments_start, total]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.invoice_number.trim()) { toast.error('Numero de facture requis'); return; }
    if (!items.some(i => i.description.trim())) { toast.error('Au moins une ligne requise'); return; }

    setSaving(true);
    try {
      const payload = {
        ...form,
        items: items.filter(i => i.description.trim()).map(i => ({
          description: i.description,
          quantity: i.quantity,
          unit_price: i.unit_price,
          total: i.quantity * i.unit_price,
        })),
        installments: form.installments_enabled ? installments : null,
      };
      const res = await createManualInvoice(payload);
      setCreatedId(res.data.id);
      toast.success(`Facture ${form.invoice_number} creee`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!createdId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/manual-invoices/${createdId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Erreur PDF');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `facture_${form.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Erreur telechargement PDF');
    }
  };

  const handleNewInvoice = () => {
    setCreatedId(null);
    setForm({ ...form, invoice_number: '', client_name: '', client_address: '', client_email: '', notes: '' });
    setItems([{ description: '', quantity: 1, unit_price: 0 }]);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="manual-invoice-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/app/invoices')}><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Facture manuelle</h1>
            <p className="text-sm text-slate-500">Mode libre — saisissez toutes les informations vous-meme</p>
          </div>
        </div>
      </div>

      {createdId ? (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-8 text-center space-y-4">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
              <FileText className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-emerald-800">Facture {form.invoice_number} creee</h2>
            <p className="text-emerald-700">{form.client_name} — {fmt(total)}</p>
            <div className="flex justify-center gap-3">
              <Button onClick={handleDownloadPdf} className="bg-emerald-600 hover:bg-emerald-700" data-testid="download-manual-pdf">
                <Download className="w-4 h-4 mr-2" />Telecharger le PDF
              </Button>
              <Button variant="outline" onClick={handleNewInvoice}>Nouvelle facture</Button>
              <Button variant="outline" onClick={() => navigate('/app/invoices')}>Retour aux factures</Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Header info */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-slate-200">
              <CardHeader className="pb-3"><CardTitle className="text-base">Facture</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Numero de facture *</Label>
                  <Input value={form.invoice_number} onChange={e => setForm({ ...form, invoice_number: e.target.value })}
                    placeholder="FAC-2025-001" required data-testid="manual-inv-number" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Date de facture</Label>
                    <Input type="date" min="2020-01-01" max="2030-12-31" value={form.invoice_date} onChange={e => setForm({ ...form, invoice_date: e.target.value })} />
                  </div>
                  <div>
                    <Label>Echeance</Label>
                    <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Devise</Label>
                    <Select value={form.currency} onValueChange={v => setForm({ ...form, currency: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CHF">CHF</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Statut</Label>
                    <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Brouillon</SelectItem>
                        <SelectItem value="sent">Envoyee</SelectItem>
                        <SelectItem value="paid">Payee</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.vat_enabled} onChange={e => setForm({ ...form, vat_enabled: e.target.checked })} className="rounded" />
                  <span className="text-sm text-slate-700">TVA (8.1%)</span>
                </label>
                <div className="pt-2 border-t border-slate-100">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.is_acquittee} onChange={e => setForm({ ...form, is_acquittee: e.target.checked, status: e.target.checked ? 'paid' : 'sent' })} className="rounded" data-testid="acquittee-toggle" />
                    <span className="text-sm font-medium text-emerald-700">Facture acquittee (preuve de paiement)</span>
                  </label>
                  {form.is_acquittee && (
                    <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-emerald-700">Date du paiement *</Label>
                          <Input type="date" min="2020-01-01" max="2030-12-31" value={form.payment_date} onChange={e => setForm({ ...form, payment_date: e.target.value })} data-testid="acquittee-date" />
                        </div>
                        <div>
                          <Label className="text-xs text-emerald-700">Moyen de paiement</Label>
                          <Select value={form.payment_method_used} onValueChange={v => setForm({ ...form, payment_method_used: v })}>
                            <SelectTrigger data-testid="acquittee-method"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="virement">Virement bancaire</SelectItem>
                              <SelectItem value="twint">Twint</SelectItem>
                              <SelectItem value="cash">Especes</SelectItem>
                              <SelectItem value="carte">Carte bancaire</SelectItem>
                              <SelectItem value="paypal">PayPal</SelectItem>
                              <SelectItem value="cheque">Cheque</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-emerald-700">Reference de paiement</Label>
                        <Input value={form.payment_reference} onChange={e => setForm({ ...form, payment_reference: e.target.value })}
                          placeholder="N° de transaction, reference bancaire..." data-testid="acquittee-ref" />
                      </div>
                      <p className="text-[10px] text-emerald-600">Conforme art. 958f CO — Le PDF portera la mention "ACQUITTEE" avec la date et le moyen de paiement. Conservation 10 ans obligatoire.</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="pb-3"><CardTitle className="text-base">Client</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Nom / Entreprise *</Label>
                  <Input value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })}
                    placeholder="Nom du client ou de l'entreprise" required data-testid="manual-client-name" />
                </div>
                <div>
                  <Label>Adresse</Label>
                  <Textarea value={form.client_address} onChange={e => setForm({ ...form, client_address: e.target.value })}
                    placeholder="Rue, NPA Ville, Pays" rows={3} />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={form.client_email} onChange={e => setForm({ ...form, client_email: e.target.value })}
                    placeholder="client@email.com" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Items */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Lignes de facture</CardTitle>
                <Button type="button" variant="outline" size="sm" onClick={addItem}><Plus className="w-3 h-3 mr-1" />Ligne</Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-slate-500 px-1">
                  <span className="col-span-6">Description</span>
                  <span className="col-span-2 text-center">Quantite</span>
                  <span className="col-span-2 text-center">Prix unitaire</span>
                  <span className="col-span-1 text-right">Total</span>
                  <span className="col-span-1"></span>
                </div>
                {items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    <Input className="col-span-6 h-9 text-sm" value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)}
                      placeholder="Description du produit ou service" data-testid={`item-desc-${idx}`} />
                    <Input className="col-span-2 h-9 text-sm text-center" type="number" min="0" step="0.5"
                      value={item.quantity || ''} onChange={e => updateItem(idx, 'quantity', e.target.value)} />
                    <Input className="col-span-2 h-9 text-sm text-center" type="number" min="0" step="0.01"
                      value={item.unit_price || ''} onChange={e => updateItem(idx, 'unit_price', e.target.value)} />
                    <span className="col-span-1 text-sm font-medium text-right text-slate-700">{fmt(item.quantity * item.unit_price)}</span>
                    <div className="col-span-1 text-center">
                      {items.length > 1 && (
                        <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => removeItem(idx)}><Trash2 className="w-3 h-3" /></Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="mt-4 pt-4 border-t border-slate-200 space-y-1 text-right">
                <div className="flex justify-end gap-8 text-sm">
                  <span className="text-slate-500">Sous-total</span>
                  <span className="font-medium w-32">{fmt(subtotal)}</span>
                </div>
                {form.vat_enabled && (
                  <div className="flex justify-end gap-8 text-sm">
                    <span className="text-slate-500">TVA {vatRate}%</span>
                    <span className="font-medium w-32">{fmt(vatAmount)}</span>
                  </div>
                )}
                {!form.vat_enabled && (
                  <div className="flex justify-end text-xs text-slate-400 italic">
                    Non assujetti a la TVA selon art. 10 LTVA
                  </div>
                )}
                <div className="flex justify-end gap-8 text-lg font-bold pt-1 border-t border-slate-200">
                  <span className="text-slate-700">Total</span>
                  <span className="w-32 text-blue-700">{fmt(total)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment terms */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3"><CardTitle className="text-base">Conditions de paiement</CardTitle></CardHeader>
            <CardContent>
              <PaymentTermsField
                value={form.payment_terms}
                onChange={v => setForm({ ...form, payment_terms: v })}
                methods={form.accepted_payment_methods}
                onMethodsChange={v => setForm({ ...form, accepted_payment_methods: v })}
              />
            </CardContent>
          </Card>

          {/* Installments */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Echelonnement de paiement</CardTitle>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.installments_enabled} onChange={e => setForm({ ...form, installments_enabled: e.target.checked })} className="rounded" data-testid="installments-toggle" />
                  <span className="text-sm text-slate-600">Activer</span>
                </label>
              </div>
            </CardHeader>
            {form.installments_enabled && (
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nombre de mensualites</Label>
                    <div className="flex items-center gap-2">
                      <Input type="number" min="2" max="12" value={form.installments_count}
                        onChange={e => setForm({ ...form, installments_count: Math.min(12, Math.max(2, parseInt(e.target.value) || 2)) })}
                        className="w-20" data-testid="installments-count" />
                      <div className="flex gap-1">
                        {[2, 3, 4, 6, 12].map(n => (
                          <button key={n} type="button" onClick={() => setForm({ ...form, installments_count: n })}
                            className={`text-xs px-2 py-1 rounded-md border transition-colors ${form.installments_count === n ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-500 border-slate-200 hover:border-blue-300'}`}>
                            {n}x
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label>Premiere echeance</Label>
                    <Input type="date" min="2020-01-01" max="2030-12-31" value={form.installments_start}
                      onChange={e => setForm({ ...form, installments_start: e.target.value })} data-testid="installments-start" />
                  </div>
                </div>

                {installments.length > 0 && (
                  <div className="bg-slate-50 rounded-lg border border-slate-200 overflow-hidden">
                    <div className="grid grid-cols-3 gap-0 text-xs font-semibold text-slate-500 bg-slate-100 px-4 py-2">
                      <span>Echeance</span><span className="text-center">Date</span><span className="text-right">Montant</span>
                    </div>
                    {installments.map((inst, i) => (
                      <div key={i} className="grid grid-cols-3 gap-0 text-sm px-4 py-2 border-t border-slate-100">
                        <span className="text-slate-600 flex items-center gap-2">
                          <Calendar className="w-3 h-3 text-slate-400" />
                          {inst.num}/{installments.length}
                        </span>
                        <span className="text-center text-slate-600">{new Date(inst.date).toLocaleDateString('fr-CH')}</span>
                        <span className="text-right font-semibold text-slate-800">{fmt(inst.amount)}</span>
                      </div>
                    ))}
                    <div className="grid grid-cols-3 gap-0 text-sm font-bold px-4 py-2 border-t-2 border-blue-200 bg-blue-50">
                      <span className="text-slate-700">Total</span>
                      <span></span>
                      <span className="text-right text-blue-700">{fmt(installments.reduce((s, i) => s + i.amount, 0))}</span>
                    </div>
                  </div>
                )}
                <p className="text-[10px] text-slate-400">Le tableau d'echelonnement apparaitra sur le PDF de la facture</p>
              </CardContent>
            )}
          </Card>

          {/* Notes */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3"><CardTitle className="text-base">Notes</CardTitle></CardHeader>
            <CardContent>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                placeholder="Notes visibles sur la facture..." rows={3} />
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate('/app/invoices')}>Annuler</Button>
            <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="create-manual-invoice-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Creer la facture
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ManualInvoicePage;
